import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

type CommonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconOnly?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
};

function classes(
  variant: ButtonVariant | undefined,
  size: ButtonSize | undefined,
  iconOnly: boolean | undefined,
  className: string | undefined,
): string {
  const v = variant ?? 'secondary';
  const s = size ?? 'md';
  const list = ['btn', `btn-${v}`];
  if (s === 'sm') list.push('btn-sm');
  if (s === 'lg') list.push('btn-lg');
  if (iconOnly) list.push('btn-icon');
  if (className) list.push(className);
  return list.join(' ');
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & CommonProps;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, iconOnly, leftIcon, rightIcon, className, children, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={classes(variant, size, iconOnly, className)}
      {...rest}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  );
});

export type ButtonLinkProps = LinkProps & CommonProps;

export function ButtonLink({
  variant,
  size,
  iconOnly,
  leftIcon,
  rightIcon,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link className={classes(variant, size, iconOnly, className)} {...rest}>
      {leftIcon}
      {children}
      {rightIcon}
    </Link>
  );
}

export type ButtonAnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & CommonProps;

export function ButtonAnchor({
  variant,
  size,
  iconOnly,
  leftIcon,
  rightIcon,
  className,
  children,
  ...rest
}: ButtonAnchorProps) {
  return (
    <a className={classes(variant, size, iconOnly, className)} {...rest}>
      {leftIcon}
      {children}
      {rightIcon}
    </a>
  );
}
